// Persisted light/dark theme store (wiki/feature-design-system.md §Theming,
// AC5). The active theme is driven by a `data-theme` attribute on the document
// root; the neutral-base tokens in `app.css` swap off that attribute. The
// choice is persisted in localStorage so it survives reloads, and an inline
// boot script in `app.html` applies the stored value before first paint to
// avoid a flash of the wrong theme.

import { writable, type Writable } from 'svelte/store';
import { browser } from '$app/environment';

export type Theme = 'light' | 'dark';

/** localStorage key — must match the inline boot script in `app.html`. */
export const THEME_STORAGE_KEY = 'voicedojo:theme';

function readInitial(): Theme {
  if (!browser) return 'light';
  // The boot script has already set data-theme; trust the DOM as the source of
  // truth so the store and the rendered theme never disagree.
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr;
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    // Storage blocked, or a test runtime whose `localStorage` global is a stub
    // that reads nothing — the write path already tolerates this; so does the
    // read, rather than throwing at import time and taking the barrel with it.
    return 'light';
  }
}

function createThemeStore(): Writable<Theme> & { toggle: () => void } {
  const store = writable<Theme>(readInitial());

  function apply(theme: Theme): void {
    if (!browser) return;
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Private-mode / storage-disabled — the attribute still applies for the
      // session, we just can't persist it. Non-fatal.
    }
  }

  return {
    subscribe: store.subscribe,
    set(theme: Theme) {
      apply(theme);
      store.set(theme);
    },
    update(fn) {
      store.update((current) => {
        const next = fn(current);
        apply(next);
        return next;
      });
    },
    toggle() {
      this.update((t) => (t === 'dark' ? 'light' : 'dark'));
    },
  };
}

export const theme = createThemeStore();
